import type { HelpStep } from "newui/components/ui/Help";

/**
 * Guided tour steps for "Allocate roles to several users" (PRO-730 topic
 * chooser) — the bulk `AssignWizard` entry point.
 *
 * Flow:
 *   1. Highlight "Allocate Roles" → open the wizard.
 *   2. The wizard as a whole (Step 1 — pick users + a role/template).
 *   3. "Next" — advances the tour's own copy of the wizard to Step 2 so the
 *      following step's target (only rendered on Step 2) is reachable.
 *   4. "Confirm assignment" — closes the wizard once the tour moves past it.
 *
 * The wizard opens via the same `AssignWizard` used by the real "Allocate
 * Roles" button — no dummy user is required here (the wizard's own Step 1
 * user picker is demonstrated empty, exactly as a real admin would see it).
 */
export interface AllocateRolesSteps {
	advanceWizardToScopeStep: () => void;
	closeAllocateRoles: () => void;
	openAllocateRoles: () => void;
}

export const buildAllocateRolesSteps = ({
	openAllocateRoles,
	advanceWizardToScopeStep,
	closeAllocateRoles,
}: AllocateRolesSteps): HelpStep[] => [
	{
		target: '[data-testid="open-assign-wizard"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Allocate roles to several users</h3>
				<p>
					Use this to assign the same role or template to many users at once.
					We&apos;ll open the wizard for you on the next step.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
		onAfterStep: () => {
			openAllocateRoles();
		},
	},
	{
		target: '[data-testid="assign-wizard"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Pick users and a role</h3>
				<p>
					Search and select as many users as you like, then choose a role
					template or an individual role to apply to all of them.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		spotlightPadding: 12,
	},
	{
		target: '[data-testid="wizard-next"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Move on to scoping</h3>
				<p>
					Once you&apos;re happy with your selection, click Next to choose where
					the role applies.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		onAfterStep: () => {
			advanceWizardToScopeStep();
		},
	},
	{
		target: '[data-testid="wizard-confirm"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Confirm the assignment</h3>
				<p>
					Review your picks and click Confirm assignment to apply the role to
					every selected user.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		onAfterStep: () => {
			closeAllocateRoles();
		},
	},
];
